import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Crown, Trophy, Medal, Award, TrendingUp, AlertCircle, Swords, Bot, Users } from 'lucide-react';
import { formatRating, getRatingColor } from '@/lib/utils';
import { ChessUser } from '@/types';

const sortOptions = [
  { value: 'rapid', label: 'Rapid', icon: TrendingUp },
  { value: 'blitz', label: 'Blitz', icon: Trophy },
  { value: 'bullet', label: 'Bullet', icon: Medal },
  { value: 'puzzles', label: 'Puzzles', icon: Award },
];

export const Leaderboard: React.FC = () => {
  const [sortBy, setSortBy] = useState('rapid');
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery(
    ['leaderboard', sortBy],
    () => apiClient.getLeaderboard(sortBy),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  );

  // Check if current user has a Chess.com profile - moved to top level
  const { data: userProfile } = useQuery(
    'userProfile',
    () => apiClient.getUserProfile(),
    {
      retry: false,
      onError: () => {
        // User doesn't have a profile yet
      }
    }
  );

  const getPositionIcon = (position: number) => {
    if (position === 1) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (position === 2) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (position === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-sm font-medium text-gray-500">{position}</span>;
  };

  const getPositionBadge = (position: number) => {
    if (position === 1) return 'bg-yellow-100 text-yellow-800';
    if (position === 2) return 'bg-gray-100 text-gray-800';
    if (position === 3) return 'bg-amber-100 text-amber-800';
    return 'bg-gray-50 text-gray-600';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
          <div className="animate-pulse bg-gray-200 h-10 w-32 rounded"></div>
        </div>
        <div className="grid gap-4">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="bg-gray-200 h-12 w-12 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="bg-gray-200 h-4 w-32 rounded"></div>
                  <div className="bg-gray-200 h-3 w-24 rounded"></div>
                </div>
                <div className="bg-gray-200 h-8 w-16 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error loading leaderboard</h2>
        <p className="text-gray-600 mb-4">Failed to load the leaderboard data.</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  const users = data?.data?.users || [];
  const currentUserHasChessProfile = userProfile?.data?.chessUsername;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leaderboard</h1>
          <p className="text-gray-600 mt-1">
            Top players ranked by {sortOptions.find(opt => opt.value === sortBy)?.label.toLowerCase()} rating
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          Refresh
        </Button>
      </div>

      {/* Quick Play Buttons */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 border border-blue-100">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Quick Play</h2>
          <p className="text-gray-600">
            Jump into a game right now on Chess.com
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Play Bot Button */}
          <div className="group cursor-pointer" onClick={() => {
            window.open('https://www.chess.com/play/computer', '_blank', 'noopener,noreferrer');
          }}>
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-blue-200 group-hover:border-blue-400 group-hover:shadow-md transition-all duration-200 hover:scale-105">
              <div className="text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <Bot className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Play Bot</h3>
                <p className="text-sm text-gray-600 mb-3">Practice & Learn</p>
                <div className="text-xs text-blue-600 font-medium">vs Computer</div>
              </div>
            </div>
          </div>

          {/* Play Random Rated Button */}
          <div className="group cursor-pointer" onClick={() => {
            window.open('https://www.chess.com/play/online/new?action=createLiveChallenge&base=600&timeIncrement=0&rated=rated', '_blank', 'noopener,noreferrer');
          }}>
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-green-200 group-hover:border-green-400 group-hover:shadow-md transition-all duration-200 hover:scale-105">
              <div className="text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Play Rated</h3>
                <p className="text-sm text-gray-600 mb-3">10 min • Live Game</p>
                <div className="text-xs text-green-600 font-medium">Affects Rating</div>
              </div>
            </div>
          </div>

          {/* Play Random Unrated Button */}
          <div className="group cursor-pointer" onClick={() => {
            window.open('https://www.chess.com/play/online/new?action=createLiveChallenge&base=600&timeIncrement=0&rated=unrated', '_blank', 'noopener,noreferrer');
          }}>
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-purple-200 group-hover:border-purple-400 group-hover:shadow-md transition-all duration-200 hover:scale-105">
              <div className="text-center">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-200 transition-colors">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Play Unrated</h3>
                <p className="text-sm text-gray-600 mb-3">10 min • Live Game</p>
                <div className="text-xs text-purple-600 font-medium">No Rating Risk</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sort Options */}
      <Card>
        <CardHeader>
          <CardTitle>Sort by Rating Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {sortOptions.map((option) => {
              const Icon = option.icon;
              return (
                <Button
                  key={option.value}
                  variant={sortBy === option.value ? 'primary' : 'outline'}
                  className="justify-start"
                  onClick={() => setSortBy(option.value)}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {option.label}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle>Top Players</CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No players yet</h3>
              <p className="text-gray-600">Be the first to join the leaderboard!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user: ChessUser, index: number) => (
                <div
                  key={user._id}
                  className="flex items-center space-x-4 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  {/* Position */}
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getPositionBadge(index + 1)}`}>
                    {getPositionIcon(index + 1)}
                  </div>

                  {/* User Info */}
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="flex-shrink-0">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        @{user.chessUsername}
                      </p>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="text-right">
                    <p className={`text-lg font-bold ${getRatingColor(user.stats[sortBy as keyof typeof user.stats] as number)}`}>
                      {formatRating(user.stats[sortBy as keyof typeof user.stats] as number)}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">{sortBy}</p>
                  </div>

                  {/* Challenge Button - Only show for users with Chess.com profiles */}
                  {user.chessUsername && (
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const challengeUrl = `https://www.chess.com/play/online/new?opponent=${user.chessUsername}`;
                          window.open(challengeUrl, '_blank', 'noopener,noreferrer');
                        }}
                        className="flex items-center space-x-1 text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                      >
                        <Swords className="h-3 w-3" />
                        <span>Challenge</span>
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect Chess.com Profile Section */}
      {!currentUserHasChessProfile && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Connect Your Chess.com Profile</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-orange-800">
                You haven't connected your Chess.com profile yet. Connect it to join the leaderboard and compete with your colleagues!
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => navigate('/chess-setup')}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Connect Chess.com Profile
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/profile')}
                >
                  View Profile
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 